import React from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, Button, Card, H1, H2, HStack, Icon, Separator, Text, VStack } from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';
import { SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../features/track';
import { formatTaxFilingStatus } from '../../Shared/graphql/utils';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../Shared/urlBuilder';
import { FinancialProfileDetailsDocument } from '../graphql/query.financialProfileDetails.generated';

export const InformationConfirmationStep: React.FC<StepProps> = ({ goToNextStep, goToPreviousStep, flowState }) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(FinancialProfileDetailsDocument, {
    throwOnError: true,
  });

  if (!data?.financialProfile || !data?.user) return null;

  const {
    financialProfile: {
      profession: { employmentStatus, occupation },
      annualPretaxIncome,
      householdInvestableAssets,
      taxFilingStatus,
      spouse,
    },
    user: { residentialAddress },
    netWorth,
  } = data;

  const occupationName = occupation || '-';

  const annualPretaxIncomeAmount = formatMoney(annualPretaxIncome, 'defaultDollar');

  const spouseAnnualPretaxIncomeAmount = spouse?.annualPretaxIncome
    ? formatMoney(spouse.annualPretaxIncome, 'defaultDollar')
    : '-';
  const householdInvestableAssetsName = formatMoney(householdInvestableAssets, 'defaultDollar');
  const taxFilingStatusName = formatTaxFilingStatus(taxFilingStatus);

  const banQualifyingAmount = 10000000;
  const bettermentBalanceCents = netWorth?.internalSubtotal?.balance.cents || 0;

  const shouldShowBalanceWarning =
    bettermentBalanceCents < banQualifyingAmount && householdInvestableAssets.cents < banQualifyingAmount;

  return (
    <WorkflowLayout
      feature="advisor_network"
      pageTitle="Confirm Your Information"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box
        css={{
          display: 'grid',
          gap: 'space.400',
          alignSelf: 'center',
          paddingTop: 'space.100',
          maxWidth: '540px',
          smDown: {
            display: 'grid',
            gap: 'space.200',
            maxWidth: '100%',
          },
        }}
      >
        <Box>
          <VStack css={{ gap: 'space.200' }}>
            <H1 variant="pageTitle">Confirm your information.</H1>
            <Text>Our specialist team will use this to connect you with the right advisors.</Text>
          </VStack>
        </Box>
        <Card
          css={{
            borderWidth: 'thin',
            borderStyle: 'solid',
            borderColor: 'border',
            borderRadius: 'medium',
          }}
        >
          <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
            <H2 variant="title200">Financial information</H2>
            <SambaAutoLink
              to={urlBuilder.appFinancialProfileEdit.absolute({ return_to: window.location.pathname })}
              data-testid="financialEditLink"
              onClick={() => {
                trackClick({ name: 'EditInformation', flow: 'BAN' });
              }}
            >
              Edit
            </SambaAutoLink>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content
            className={vstack({
              alignItems: 'stretch',
              padding: 'space.300',
            })}
          >
            {shouldShowBalanceWarning && (
              <Box>
                <Banner variant="warning" css={{ mb: 'space.200' }}>
                  <Text variant="text50">
                    It looks like you may not meet the $100,000 minimum balance required for the Betterment Advisor
                    Network.
                  </Text>
                </Banner>
              </Box>
            )}
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: 'auto auto auto',
                gap: 'space.200',
              }}
            >
              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Employment status
                </Text>
                <Text>{employmentStatus}</Text>
              </VStack>
              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Occupation
                </Text>
                <Text>{occupationName}</Text>
              </VStack>

              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Annual pretax income
                </Text>
                <Text>{annualPretaxIncomeAmount}</Text>
              </VStack>
              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Spouse annual pretax income
                </Text>
                <Text>{spouseAnnualPretaxIncomeAmount}</Text>
              </VStack>

              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Household investable assets
                </Text>
                <HStack css={{ gap: 'space.50' }}>
                  <Text>{householdInvestableAssetsName}</Text>

                  {shouldShowBalanceWarning && (
                    <Icon
                      name="WarningFilled"
                      size="xsmall"
                      color="icon.warning"
                      css={{ marginTop: 'space.25' }}
                      data-testid="warningIcon"
                    />
                  )}
                </HStack>
              </VStack>
              <VStack css={{ gap: 'space.25' }}>
                <Text variant="text50" color="text.weak">
                  Tax filing status
                </Text>
                <Text>{taxFilingStatusName}</Text>
              </VStack>
            </Box>
          </Card.Content>
        </Card>
        <Card css={{ borderWidth: 'thin', borderStyle: 'solid', borderColor: 'border', borderRadius: 'medium' }}>
          <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
            <H2 variant="title200"> Location </H2>
            <SambaAutoLink
              to={urlBuilder.appPersonalInfoEdit.absolute({ return_to: window.location.pathname })}
              data-testid="locationEditLink"
              onClick={() => {
                trackClick({ name: 'EditLocation', flow: 'BAN' });
              }}
            >
              Edit
            </SambaAutoLink>
          </Card.Header>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Card.Content css={{ padding: 'space.300' }}>
            <VStack css={{ gap: 'space.25' }}>
              <Text variant="text50" color="text.weak">
                Address
              </Text>
              <VStack css={{ gap: 'space.0' }}>
                <Text>{residentialAddress?.line1}</Text>
                <Text>
                  {residentialAddress?.city}, {residentialAddress?.state.code} {residentialAddress?.postalCode}
                </Text>
              </VStack>
            </VStack>
          </Card.Content>
        </Card>
        <Button
          onClick={() => {
            trackClick({ name: 'ConfirmInformation', flow: 'BAN' });
            goToNextStep();
          }}
          variant="solidPrimary"
          css={{
            maxWidth: '1/4',
            smDown: { width: '100%' },
            paddingY: 'space.200',
          }}
        >
          Continue
        </Button>
      </Box>
    </WorkflowLayout>
  );
};
